import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Typed MM/DD/YYYY date field - the drop-in replacement for <input type="date">.
 *
 * There is no calendar: every date in this app is keyed, and the native picker
 * cost two extra clicks per field on 66 fields. What it buys instead is the year
 * shift register below - two keystrokes for a current-century year.
 *
 * It is a DROP-IN: `value` and `onChange` carry the same 'YYYY-MM-DD' string the
 * native input carried, and onChange still hands back an event-shaped
 * `{ target: { value } }`, so every popup's state, validation and submit path is
 * untouched. Extra props (max, className, disabled, id, ...) pass through to the
 * DOM node verbatim, which keeps the two popup style families pixel-identical
 * and keeps `max` assertable - but note `max` is ADVISORY here, exactly as it
 * was on the native input (it never blocked typing a later date either).
 * capToMaxDate in reports/lib/reportRequest.js is the real ceiling.
 *
 * INVARIANT: onChange fires only with a complete, calendar-valid date or with
 * '' for a deliberate clear - never with a half-typed string. Half the range
 * popups validate with `new Date(start) > new Date(end)`, which is false
 * against an Invalid Date, so a partial would slip a bad window past
 * validation silently. Leaving a draft incomplete keeps the last committed
 * value and restores it on blur, which is what the native input did too.
 */

export const YEAR_SEED = '20';

/** The year is a 4-wide shift register seeded with '20': each digit shifts the
 *  register left and appends. '20' + 2 -> '202' + 6 -> '2026' is the two-
 *  keystroke common case; a fifth digit slides the seed off the front
 *  ('2026' + 5 -> '0265') instead of being swallowed, and typing all four
 *  digits still lands anywhere ('20' + 1,9,9,9 -> '201','2019','0199','1999'). */
export function shiftYear(register, digit) {
  return ((register || YEAR_SEED) + digit).slice(-4);
}

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const EMPTY = { mm: '', dd: '', yr: '' };
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MDY_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

function daysInMonth(year, month) {
  if (month !== 2) return MONTH_DAYS[month - 1];
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
}

/** Day ceiling while typing: the year is entered last, so February gets the
 *  leap 29 and a non-leap 02/29 is caught at emit time by `toIso`. */
function typedDayCap(mm) {
  return mm === '02' ? 29 : MONTH_DAYS[Number(mm) - 1];
}

function toDraft(iso) {
  const m = ISO_RE.exec(iso || '');
  return m ? { mm: m[2], dd: m[3], yr: m[1] } : EMPTY;
}

function toDisplay({ mm, dd, yr }) {
  if (mm.length < 2) return mm;
  if (dd.length < 2) return `${mm}/${dd}`;
  return `${mm}/${dd}/${yr || YEAR_SEED}`;
}

/** ISO for a complete, calendar-valid draft; '' while it is still partial. */
function toIso({ mm, dd, yr }) {
  if (yr.length < 4) return '';
  const day = Number(dd);
  return day >= 1 && day <= daysInMonth(Number(yr), Number(mm)) ? `${yr}-${mm}-${dd}` : '';
}

function isEmpty({ mm, dd, yr }) {
  return !mm && !dd && !yr;
}

function pushDigit(draft, d) {
  const { mm, dd } = draft;
  if (mm.length < 2) {
    // A digit no second digit could follow completes the segment: 7 -> 07, and
    // in February 3 -> 03 (there is no 30th to wait for).
    if (!mm) return { ...draft, mm: Number(d) * 10 > 12 ? `0${d}` : d };
    return mm + d >= '01' && mm + d <= '12' ? { ...draft, mm: mm + d } : draft;
  }
  if (dd.length < 2) {
    if (!dd) return { ...draft, dd: Number(d) * 10 > typedDayCap(mm) ? `0${d}` : d };
    const next = dd + d;
    return Number(next) >= 1 && Number(next) <= typedDayCap(mm) ? { ...draft, dd: next } : draft;
  }
  return { ...draft, yr: shiftYear(draft.yr, d) };
}

function popDigit(draft) {
  const { mm, dd, yr } = draft;
  // Backspacing the year mirrors typing it: the two digits that displaced the
  // seed come back off, then the register rests at '20' again ('' here).
  if (yr) return { ...draft, yr: yr.length > 3 ? yr.slice(0, -1) : '' };
  if (dd) return { ...draft, dd: dd.slice(0, -1) };
  return { ...draft, mm: mm.slice(0, -1) };
}

const isAllSelected = (el) =>
  el.value.length > 0 && el.selectionStart === 0 && el.selectionEnd === el.value.length;

const MaskedDateField = ({ value, onChange, placeholder = 'MM/DD/YYYY', ...rest }) => {
  const [draft, setDraft] = useState(() => toDraft(value));
  const inputRef = useRef(null);
  const focusedRef = useRef(false);
  const display = toDisplay(draft);

  // The draft owns the field while it has focus; the prop owns it otherwise -
  // every popup re-seeds its dates in a useEffect when it opens.
  useEffect(() => {
    if (!focusedRef.current) setDraft(toDraft(value));
  }, [value]);

  // Digits always land at the end, but React restores the pre-render caret
  // offset on a controlled input, which would strand it mid-mask.
  useLayoutEffect(() => {
    if (focusedRef.current) inputRef.current.setSelectionRange(display.length, display.length);
  }, [display]);

  const commit = (next) => {
    setDraft(next);
    const iso = toIso(next);
    if (iso) {
      if (iso !== value) onChange({ target: { value: iso } });
    } else if (isEmpty(next) && value) {
      onChange({ target: { value: '' } });
    }
  };

  const handleKeyDown = (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      commit(isAllSelected(e.target) ? EMPTY : popDigit(draft));
    } else if (e.key.length === 1 && e.key >= '0' && e.key <= '9') {
      e.preventDefault();
      commit(pushDigit(isAllSelected(e.target) ? EMPTY : draft, e.key));
    }
  };

  // Residual path: paste, autofill, and the popup tests, which drive these
  // fields with the ISO string the field itself emits. Anything unrecognised is
  // dropped - React re-renders the mask over it.
  const handleChange = (e) => {
    const raw = e.target.value.trim();
    const iso = ISO_RE.exec(raw);
    const mdy = MDY_RE.exec(raw);
    if (raw === '') commit(EMPTY);
    else if (iso) commit({ mm: iso[2], dd: iso[3], yr: iso[1] });
    else if (mdy) commit({ mm: mdy[1], dd: mdy[2], yr: mdy[3] });
  };

  const handleFocus = (e) => {
    focusedRef.current = true;
    e.target.select(); // focus-and-type re-enters the whole date
  };

  // A half-typed draft is not a value: restore the last committed one, which is
  // what the native input did and what keeps a required field from emptying
  // itself when the user tabs away mid-edit.
  const handleBlur = () => {
    focusedRef.current = false;
    setDraft(toDraft(value));
  };

  return (
    <input
      {...rest}
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={placeholder}
      value={display}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

export default MaskedDateField;
